import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { usePrescription } from "./PrescriptionContext";
import AppSelect from "../../../../../../shared/AppSelect";
import { useIsMobile } from "../../../../../../shared/responsive";
import { formatFixedNumber, formatPercent } from "../../../../../../shared/numberFormat";
import { getAmbientDisplayClassNumber } from "../Ambients/ambientClassPresentation";
import "./PrescriptionForm.scss";

const clsPresc = {
  0: "mbp",
  1: "bp",
  2: "pp",
  3: "ap",
  4: "map",
  5: "five",
  6: "six",
};

const sortOrder = [
  "six",
  "five",
  "map",
  "ap",
  "pp",
  "bp",
  "mbp",
];

const PRODUCT_FIELDS = [
  {
    key: "prod1",
    column: 0,
    defaultUnit: "10",
    labelKey: "prescription:creation.form.product",
    labelIndex: 1,
    units: ["10", "20", "30", "40", "50"],
  },
  {
    key: "prod2",
    column: 1,
    defaultUnit: "40",
    labelKey: "prescription:creation.form.product",
    labelIndex: 2,
    units: ["40", "50"],
  },
  {
    key: "prod3",
    column: 2,
    defaultUnit: "50",
    labelKey: "prescription:creation.form.product",
    labelIndex: 3,
    units: ["50", "40"],
  },
];

const getColor = (key, numClasses) => {
  return {
    2: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(26,150,65)",
    },
    3: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(255,255,192)",
      pp: "rgb(26,150,65)",
    },
    4: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(254,201,129)",
      pp: "rgb(196,230,135)",
      ap: "rgb(26,150,65)",
    },
    5: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(253,174,97)",
      pp: "rgb(255,255,192)",
      ap: "rgb(166,217,106)",
      map: "rgb(26,150,65)",
    },
    6: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(246,144,83)",
      pp: "rgb(255,223,154)",
      ap: "rgb(220,240,158)",
      map: "rgb(138,204,98)",
      five: "rgb(26,150,65)",
    },
    7: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(241,124,74)",
      pp: "rgb(254,201,129)",
      ap: "rgb(255,255,192)",
      map: "rgb(196,230,135)",
      five: "rgb(119,195,92)",
      six: "rgb(26,150,65)",
    },
  }[numClasses][key];
};

const getUnitLabel = (unitValue, t) => {
  const labels = {
    10: t("prescription:creation.units.seed_ha"),
    20: t("prescription:creation.units.seed_m2"),
    30: t("prescription:creation.units.seed_m"),
    40: t("prescription:creation.units.kg_ha"),
    50: t("prescription:creation.units.lt_ha"),
  };

  return labels[unitValue];
};

const getTotalUnitLabel = (unitValue, t) => {
  const labels = {
    10: t("prescription:creation.units.seed_total"),
    20: t("prescription:creation.units.seed_total"),
    30: t("prescription:creation.units.seed_total"),
    40: t("prescription:creation.units.kg"),
    50: t("prescription:creation.units.lt"),
  };

  return labels[unitValue];
};

const getProductTotal = (value, hectares, unitValue, rowSpacingCm) => {
  if (!Number.isFinite(value) || !Number.isFinite(hectares)) {
    return null;
  }

  if (unitValue === "20") {
    return value * hectares * 10000;
  }

  if (unitValue === "30") {
    if (!Number.isFinite(rowSpacingCm) || rowSpacingCm <= 0) {
      return null;
    }

    return value * hectares * 10000 / (rowSpacingCm / 100);
  }

  return value * hectares;
};

const getNumericInputValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(String(value).replace(",", "."));

  return Number.isFinite(numericValue) ? numericValue : null;
};

const UnitSelect = ({ value, options, onChange, t }) => {
  return (
    <AppSelect
      ariaLabel={t("prescription:creation.form.unit")}
      onChange={onChange}
      options={options.map((option) => ({
        label: getUnitLabel(option, t),
        value: option,
      }))}
      value={value}
    />
  );
};

const RowSpacingInput = ({ value, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className="prescription-row-spacing">
      <label className="prescription-row-spacing__label" htmlFor="prescription-row-spacing">
        {t("prescription:creation.form.row_spacing")}
      </label>
      <div className="prescription-row-spacing__control">
        <input
          id="prescription-row-spacing"
          className="input-base"
          inputMode="decimal"
          min="0"
          placeholder="52"
          step="0.01"
          type="number"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <span>cm</span>
      </div>
    </div>
  );
};

const collectRings = (geometry) => {
  if (!geometry) {
    return [];
  }

  if (geometry.type === "Polygon") {
    return geometry.coordinates.slice(0, 1);
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.flatMap((polygon) => polygon.slice(0, 1));
  }

  return [];
};

const PrescriptionAmbientPreview = ({ ambient, numClasses }) => {
  const { t } = useTranslation();
  const preview = useMemo(() => {
    const features = ambient?.geojson?.features || [];
    const shapes = [];
    const points = [];

    features.forEach((feature) => {
      collectRings(feature.geometry).forEach((ring) => {
        const validRing = ring.filter((point) => Array.isArray(point) && point.length >= 2);

        if (validRing.length < 3) {
          return;
        }

        validRing.forEach(([x, y]) => points.push({ x, y }));
        shapes.push({
          cls: feature.properties?.cls,
          ring: validRing,
        });
      });
    });

    if (points.length < 3 || shapes.length < 1) {
      return null;
    }

    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const width = maxX - minX || 1;
    const height = maxY - minY || 1;

    const padding = Math.max(width, height) * 0.04;
    const viewBox = `${-padding} ${-padding} ${width + padding * 2} ${height + padding * 2}`;

    const previewShapes = shapes.map((shape) => {
      const colorKey = clsPresc[shape.cls];
      const pointsValue = shape.ring
        .map(([x, y]) => {
          const px = x - minX;
          const py = maxY - y;
          return `${px},${py}`;
        })
        .join(" ");

      return {
        color: colorKey ? getColor(colorKey, numClasses) : "rgba(59, 130, 246, 0.35)",
        points: pointsValue,
      };
    });

    return {
      shapes: previewShapes,
      viewBox,
    };
  }, [ambient, numClasses]);

  if (!preview) {
    return null;
  }

  return (
    <div className="prescription-ambient-preview">
      <span className="prescription-ambient-preview__label">
        {t("prescription:creation.form.ambient_preview")}
      </span>
      <svg className="prescription-ambient-preview__svg" viewBox={preview.viewBox} preserveAspectRatio="xMidYMid meet">
        {preview.shapes.map((shape, index) => (
          <polygon
            key={`${shape.points}-${index}`}
            points={shape.points}
            fill={shape.color}
          />
        ))}
      </svg>
    </div>
  );
};

const ProductSummaryTables = ({ summaries }) => {
  const { t } = useTranslation();

  if (summaries.length < 1) {
    return null;
  }

  return (
    <div className="prescription-product-summaries">
      {summaries.map((summary) => (
        <section key={summary.key} className="prescription-product-summary">
          <h6 className="prescription-product-summary__title">
            {t("prescription:creation.form.product_summary_title", { product: summary.name })}
          </h6>

          <div className="prescription-product-summary__table">
            <div className="prescription-product-summary__row prescription-product-summary__row--head">
              <span>{t("prescription:creation.form.environment")}</span>
              <span>{t("prescription:creation.form.surface")}</span>
              <span>%</span>
              <span>{t("prescription:creation.form.dose", { unit: summary.unitLabel })}</span>
              <span>{t("prescription:creation.form.product_total", { unit: summary.totalUnitLabel })}</span>
            </div>

            <div className="prescription-product-summary__scale-label">
              {t("prescription:creation.form.higher_productivity")}
            </div>

            {summary.rows.map((row) => (
              <div key={`${summary.key}-${row.cls}`} className="prescription-product-summary__row">
                <span className="prescription-product-summary__environment">
                  <span className="box-cls" style={{ backgroundColor: row.color }} />
                  {row.label}
                </span>
                <span>{formatFixedNumber(row.ha)}</span>
                <span>{formatPercent(row.percent)}</span>
                <span>{formatFixedNumber(row.value)}</span>
                <span>{row.total === null ? "-" : formatFixedNumber(row.total)}</span>
              </div>
            ))}

            <div className="prescription-product-summary__scale-label prescription-product-summary__scale-label--bottom">
              {t("prescription:creation.form.lower_productivity")}
            </div>

            <div className="prescription-product-summary__row prescription-product-summary__row--total">
              <span>{t("prescription:creation.form.total")}</span>
              <span>{formatFixedNumber(summary.totalHa)}</span>
              <span>{formatPercent(100)}</span>
              <span>{formatFixedNumber(summary.weightedAverage)}</span>
              <span>{summary.totalAmount === null ? "-" : formatFixedNumber(summary.totalAmount)}</span>
            </div>
          </div>

          <div className="prescription-product-summary__meta">
            <span>
              {t("prescription:creation.form.weighted_average")}:{" "}
              <strong>{formatFixedNumber(summary.weightedAverage)} {summary.unitLabel}</strong>
            </span>
          </div>
        </section>
      ))}
    </div>
  );
};

const PrescriptionForm = ({ prescription, numClasses, ambient }) => {
  const { columns, setColumns, prod, setProd, rowSpacingCm, setRowSpacingCm, units, setUnits } = usePrescription();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const rowSpacingValue = getNumericInputValue(rowSpacingCm);
  const needsRowSpacing = useMemo(() => {
    return PRODUCT_FIELDS.some((field) => units[field.key] === "30");
  }, [units]);
  const totalHa = useMemo(() => {
    let total = 0;
    Object.keys(prescription).forEach((key) => {
      total += prescription[key].ha;
    });
    return total;
  }, [prescription]);

  const filtered = useMemo(() => {
    return sortOrder.filter((key) => prescription[key].ha > 0);
  }, [prescription]);

  const getProductLabel = useCallback(
    (field) => t(field.labelKey, {index: field.labelIndex}),
    [t]
  );

  const classRows = useMemo(() => {
    return filtered.map((key) => {
      const cls = prescription[key].cls;
      const ha = Number(prescription[key].ha) || 0;
      const percent = totalHa > 0 ? (ha * 100) / totalHa : 0;

      return {
        key,
        cls,
        ha,
        percent,
        color: getColor(key, numClasses),
        label: `${t("prescription:creation.form.class")} ${getAmbientDisplayClassNumber(cls, numClasses)}`,
      };
    });
  }, [filtered, numClasses, prescription, t, totalHa]);
  const hasAmbientPreview = (ambient?.geojson?.features || []).length > 0;

  const productSummaries = useMemo(() => {
    return PRODUCT_FIELDS.map((field) => {
      const rows = classRows.map((row) => {
        const value = getNumericInputValue(columns[field.column][row.cls]);

        return {
          ...row,
          value,
          total: value === null ? null : getProductTotal(value, row.ha, units[field.key], rowSpacingValue),
        };
      });

      const isComplete = rows.length > 0 && rows.every((row) => row.value !== null);

      if (!isComplete) {
        return null;
      }

      const totalAmount = rows.every((row) => row.total !== null)
        ? rows.reduce((acc, row) => acc + row.total, 0)
        : null;
      const weightedAverage = totalHa > 0
        ? rows.reduce((acc, row) => acc + (row.value * row.ha), 0) / totalHa
        : 0;

      return {
        key: field.key,
        name: prod[field.key] || getProductLabel(field),
        rows,
        totalAmount,
        totalHa,
        totalUnitLabel: getTotalUnitLabel(units[field.key], t),
        unitLabel: getUnitLabel(units[field.key], t),
        weightedAverage,
      };
    }).filter(Boolean);
  }, [classRows, columns, getProductLabel, prod, rowSpacingValue, t, totalHa, units]);

  const handleProdChange = (key, value) => {
    setProd((current) => {
      return { ...current, [key]: value };
    });
  };

  const handleColumnChange = (row, col, value) => {
    setColumns((current) => {
      const nextColumns = { ...current };
      nextColumns[col][row] = value;
      return nextColumns;
    });
  };

  const handleUnitChange = (productKey, value) => {
    setUnits((current) => ({
      ...current,
      [productKey]: value,
    }));
  };

  if (isMobile) {
    return (
      <div className="prescription-mobile">
        <PrescriptionAmbientPreview ambient={ambient} numClasses={numClasses} />

        <div className="prescription-mobile__summary">
          <span className="prescription-mobile__summary-label">{t("prescription:creation.form.summary_label")}</span>
          <strong className="prescription-mobile__summary-value">
            {t("prescription:creation.form.summary_value", {
              classes: filtered.length,
              ha: formatFixedNumber(totalHa),
            })}
          </strong>
        </div>

        {needsRowSpacing && (
          <RowSpacingInput value={rowSpacingCm} onChange={setRowSpacingCm} />
        )}

        <div className="prescription-mobile__classes">
          <div className="prescription-mobile__productivity-label">
            {t("prescription:creation.form.higher_productivity")}
          </div>
          {classRows.map((row) => {
            return (
              <div key={row.key} className="prescription-mobile__class-card">
                <div className="prescription-mobile__class-header">
                  <span className="box-cls" style={{ backgroundColor: row.color }} />
                  <div className="prescription-mobile__class-copy">
                    <span className="prescription-mobile__class-title">
                      {row.label}
                    </span>
                    <span className="prescription-mobile__class-meta">
                      {formatFixedNumber(row.ha)} ha - {formatPercent(row.percent)}
                    </span>
                  </div>
                </div>

                <div className="prescription-mobile__class-inputs">
                  {PRODUCT_FIELDS.map((field) => (
                    <div key={`${row.key}-${field.key}`} className="prescription-mobile__product-block">
                      <span className="prescription-mobile__product-title">{getProductLabel(field)}</span>

                      <div className="prescription-mobile__product-grid">
                        <label className="prescription-mobile__field">
                          <span className="prescription-mobile__field-label">{t("prescription:creation.form.name")}</span>
                          <input
                            type="text"
                            className="input-base"
                            value={prod[field.key]}
                            onChange={(event) => handleProdChange(field.key, event.target.value)}
                            placeholder={getProductLabel(field)}
                          />
                        </label>

                        <div className="prescription-mobile__field">
                          <span className="prescription-mobile__field-label">{t("prescription:creation.form.unit")}</span>
                          <UnitSelect
                            value={units[field.key]}
                            options={field.units}
                            onChange={(nextValue) => handleUnitChange(field.key, nextValue)}
                            t={t}
                          />
                        </div>
                      </div>

                      <label className="prescription-mobile__field">
                        <span className="prescription-mobile__field-label">{t("prescription:creation.form.quantity")}</span>
                        <input
                          className="input-base"
                          type="number"
                          value={columns[field.column][row.cls] || ""}
                          onChange={(event) => handleColumnChange(row.cls, field.column, event.target.value)}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="prescription-mobile__productivity-label prescription-mobile__productivity-label--bottom">
            {t("prescription:creation.form.lower_productivity")}
          </div>
        </div>

        <ProductSummaryTables summaries={productSummaries} />
      </div>
    );
  }

  return (
    <div className={`prescription-desktop-layout ${hasAmbientPreview ? "has-preview" : "no-preview"}`.trim()}>
      {hasAmbientPreview && (
        <div className="prescription-desktop-preview-column">
          <PrescriptionAmbientPreview ambient={ambient} numClasses={numClasses} />
        </div>
      )}

      <div className="prescription-desktop-form-column">
        <div className="prescription-entry-table">
          <div className="prescription-entry-grid prescription-desktop-products-row">
            <div className="prescription-entry-cell prescription-entry-cell--metrics-spacer" aria-hidden="true"></div>
            {PRODUCT_FIELDS.map((field) => (
              <div key={field.key} className="prescription-entry-cell">
                <input
                  type="text"
                  className="input-base"
                  value={prod[field.key]}
                  onChange={(event) => handleProdChange(field.key, event.target.value)}
                  placeholder={getProductLabel(field)}
                />
              </div>
            ))}
          </div>
          <div className="prescription-entry-grid prescription-desktop-units-row">
            <div className="prescription-entry-cell prescription-desktop-productivity-heading">
              <span className="text-title">{t("prescription:creation.form.higher_productivity")}</span>
            </div>
            <div className="prescription-entry-cell prescription-desktop-metric-heading">
              <span className="text-title">ha</span>
            </div>
            <div className="prescription-entry-cell prescription-desktop-metric-heading">
              <span className="text-title">%</span>
            </div>
            {PRODUCT_FIELDS.map((field) => (
              <div key={`${field.key}-unit`} className="prescription-entry-cell">
                <UnitSelect
                  value={units[field.key]}
                  options={field.units}
                  onChange={(nextValue) => handleUnitChange(field.key, nextValue)}
                  t={t}
                />
              </div>
            ))}
          </div>
          {needsRowSpacing && (
            <RowSpacingInput value={rowSpacingCm} onChange={setRowSpacingCm} />
          )}
          {classRows.map((row) => {
            return (
              <div key={row.key} className="prescription-entry-grid prescription-class-input-row">
                <div className="prescription-entry-cell prescription-entry-cell--color">
                  <div className="box-cls" style={{ backgroundColor: row.color }}></div>
                  <span className="prescription-entry-cell__class-label">{row.label}</span>
                </div>
                <div className="prescription-entry-cell prescription-entry-cell--number">
                  {formatFixedNumber(row.ha)}
                </div>
                <div className="prescription-entry-cell prescription-entry-cell--number">
                  {formatFixedNumber(row.percent)}
                </div>
                {PRODUCT_FIELDS.map((field) => (
                  <div key={`${row.key}-${field.key}-desktop`} className="prescription-entry-cell">
                    <input
                      className="input-base"
                      type="number"
                      value={columns[field.column][row.cls] || ""}
                      onChange={(event) => handleColumnChange(row.cls, field.column, event.target.value)}
                    />
                  </div>
                ))}
              </div>
            );
          })}
          <div className="prescription-entry-grid prescription-lower-productivity-row">
            <div className="prescription-entry-cell prescription-desktop-productivity-heading">
              <span className="text-title">{t("prescription:creation.form.lower_productivity")}</span>
            </div>
          </div>
          <div className="prescription-entry-grid prescription-total-row">
            <div className="prescription-entry-cell">
              <span className="text-title">{t("prescription:creation.form.total")}</span>
            </div>
            <div className="prescription-entry-cell prescription-entry-cell--number">
              <span className="text-title">{formatFixedNumber(totalHa)}</span>
            </div>
            <div className="prescription-entry-cell prescription-entry-cell--number">
              <span className="text-title">{formatFixedNumber(100)}</span>
            </div>
            {PRODUCT_FIELDS.map((field) => (
              <div key={`${field.key}-total-empty`} className="prescription-entry-cell" />
            ))}
          </div>
        </div>

      </div>

      {productSummaries.length > 0 && (
        <div className="prescription-desktop-summary-row">
          <ProductSummaryTables summaries={productSummaries} />
        </div>
      )}
    </div>
  );
};

export default PrescriptionForm;
